import { ar, enGB } from "date-fns/locale";
import { TranslateModule } from "@ngx-translate/core";
import { formatDistanceToNowStrict } from "date-fns";
import { toSignal } from "@angular/core/rxjs-interop";
import { Component, DestroyRef, computed, inject, input, signal } from "@angular/core";

import { TranslationService } from "src/app/core/services/translation/translation.service";
import { Languages } from "src/app/core/enums/languages";

/** Re-render the relative time on this cadence. A minute is the smallest unit the string shows. */
const TICK_MS = 60_000;

/**
 * @description
 * "Prices updated 14 minutes ago" — shown only when a screen was served from the offline cache.
 *
 * T-12 restricts this to the pricing step on purpose. A timestamp on every cached screen becomes
 * furniture the clerk stops reading; on prices it is the one place staleness costs money, so it has
 * to stay unusual enough to notice.
 *
 * Renders nothing at all when `updatedAt` is null — that is a live response, and "updated just now"
 * on every online booking would be exactly the noise this is trying to avoid.
 */
@Component({
  selector: "app-data-age",
  standalone: true,
  imports: [TranslateModule],
  template: `
    @if (age(); as age) {
      <span class="data-age" role="status">{{ "OFFLINE_STATE.UPDATED_AGO" | translate: { age } }}</span>
    }
  `,
  styles: [
    `
      .data-age {
        display: inline-block;
        font-size: 12px;
        line-height: 1.4;
        color: #6b4615;
        background: rgb(138 90 28 / 10%);
        border-radius: 999px;
        padding: 2px 10px;
      }
    `,
  ],
})
export class DataAgeComponent {
  readonly #translation = inject(TranslationService);

  /** Epoch ms the data was fetched from the server. `null` for a live response — renders nothing. */
  readonly updatedAt = input.required<number | null>();

  readonly #language = toSignal(this.#translation.currentLanguage$, { initialValue: Languages.EN as string });

  /** Advances on its own so a form left open does not keep claiming the price is a minute old. */
  readonly #now = signal(Date.now());

  readonly age = computed(() => {
    const updatedAt = this.updatedAt();
    if (!updatedAt) return null;

    // Read so the string recomputes on each tick; `formatDistanceToNowStrict` reads the clock itself.
    this.#now();

    return formatDistanceToNowStrict(updatedAt, { locale: this.#language() === Languages.AR ? ar : enGB });
  });

  constructor() {
    const timer = setInterval(() => this.#now.set(Date.now()), TICK_MS);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }
}
